import { useEffect, useRef } from 'react';

type EntityDevLoggerMode = 'on-change' | 'initial';

interface EntityDevLoggerOptions {
  enabled?: boolean;
  mode?: EntityDevLoggerMode;
}

/**
 * Dev-only helper that logs entity payloads when screens load.
 * Avoids production noise by guarding on the current environment and
 * deduplicating repeated payloads.
 */
export function useEntityDevLogger<T>(
  label: string,
  data: T | null | undefined,
  options: EntityDevLoggerOptions = {}
): void {
  const { enabled = true, mode = 'on-change' } = options;
  const lastSerializedRef = useRef<string | null>(null);
  const lastDataRef = useRef<T | null | undefined>(undefined);
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!enabled) return;

    if (data == null) {
      if (mode === 'on-change') {
        lastSerializedRef.current = null;
        lastDataRef.current = data;
      }
      return;
    }

    if (mode === 'initial' && hasLoggedRef.current) {
      return;
    }

    let serialized: string | null = null;
    let serializationFailed = false;

    try {
      serialized = JSON.stringify(data);
    } catch (error) {
      serializationFailed = true;
      console.warn(`[entity-dev] Failed to serialize payload for ${label}:`, error);
    }

    if (mode === 'on-change') {
      const alreadyLoggedViaSerialization =
        serialized !== null && lastSerializedRef.current === serialized;
      const alreadyLoggedViaReference = serializationFailed && lastDataRef.current === data;

      if (alreadyLoggedViaSerialization || alreadyLoggedViaReference) {
        return;
      }

      lastSerializedRef.current = serialized ?? '__SERIALIZATION_FAILED__';
      lastDataRef.current = data;
    }

    const groupLabel = `[entity-dev] ${label}`;

    if (typeof console.groupCollapsed === 'function') {
      console.groupCollapsed(groupLabel);
      console.log(data);
      console.groupEnd();
    } else {
      console.log(groupLabel, data);
    }

    hasLoggedRef.current = true;
  }, [data, enabled, label, mode]);
}
